import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/api";
import { drawWatermark, formatWatermarkText } from "@/utils/watermark";
import CaptureDetailsDrawer, { CaptureDetailsPanel } from "@/components/CaptureDetailsDrawer";

function StatusPill({ variant, label, value, testId }) {
  const classes =
    variant === "ok"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : variant === "warn"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <Badge data-testid={testId} variant="outline" className={`${classes} px-3 py-1 rounded-full`}>
      <span className="text-xs">{label}:</span>
      <span className="ml-1 text-xs font-medium">{value}</span>
    </Badge>
  );
}

function InlineCounter({ label, value, onInc, onDec, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-inline-counter`} className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-600" data-testid={`${testIdPrefix}-inline-label`}>{label}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`${testIdPrefix}-inline-decrement`}
            className="h-9 w-9 rounded-full border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            onClick={onDec}
          >
            -
          </button>
          <div className="min-w-[2.5rem] text-center text-sm font-semibold text-slate-900" data-testid={`${testIdPrefix}-inline-value`}>{value}</div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-inline-increment`}
            className="h-9 w-9 rounded-full border border-slate-300 bg-slate-900 text-white hover:bg-slate-800"
            onClick={onInc}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NewSessionPage() {
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const drawLoopRef = useRef(null);
  const geoWatchRef = useRef(null);

  // Auto clipping state
  const clipTimerRef = useRef(null);
  const uploadChainRef = useRef(Promise.resolve());
  const sessionIdRef = useRef(null);
  const clipIndexRef = useRef(0);
  const continueAfterStopRef = useRef(false);
  const stopResolveRef = useRef(null);

  const [activityType, setActivityType] = useState("loading");
  const [gpsStatus, setGpsStatus] = useState({ ok: false, error: "", lat: null, lng: null, accuracy: null });
  const [driveStatus, setDriveStatus] = useState(null);

  const [cases, setCases] = useState(0);
  const [sacks, setSacks] = useState(0);
  const [placeName, setPlaceName] = useState("");
  const [notes, setNotes] = useState("");

  const [hasCamera, setHasCamera] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [photos, setPhotos] = useState([]); // {blob, url, uploaded, drive_file_id}

  const [clipMinutes, setClipMinutes] = useState(5);
  const [clipIndex, setClipIndex] = useState(0);
  const [uploadQueueCount, setUploadQueueCount] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState("");

  // IMPORTANT: Canvas draw loop captures values by closure.
  // We keep a ref with the latest values so watermark updates live while recording.
  const liveRef = useRef({
    activityType,
    cases,
    sacks,
    notes,
    placeName,
    gpsStatus,
  });

  useEffect(() => {
    liveRef.current = { activityType, cases, sacks, notes, placeName, gpsStatus };
  }, [activityType, cases, sacks, notes, placeName, gpsStatus]);

  const watermarkLines = useMemo(() => {
    if (!gpsStatus.ok) return [];
    return formatWatermarkText({
      activityType,
      lat: gpsStatus.lat,
      lng: gpsStatus.lng,
      accuracy: gpsStatus.accuracy,
      cases,
      sacks,
      extraLine: [placeName, notes].filter(Boolean).join(" • "),
    });
  }, [activityType, cases, gpsStatus, sacks, notes, placeName]);

  const clearClipTimer = () => {
    if (clipTimerRef.current) {
      clearTimeout(clipTimerRef.current);
      clipTimerRef.current = null;
    }
  };

  const scheduleAutoStop = () => {
    clearClipTimer();
    const mins = Math.max(1, Number(clipMinutes) || 5);
    clipTimerRef.current = setTimeout(() => {
      const rec = recorderRef.current;
      if (rec && rec.state === "recording") {
        continueAfterStopRef.current = true;
        rec.stop();
      }
    }, mins * 60 * 1000);
  };

  const stopDrawLoop = () => {
    if (drawLoopRef.current) {
      cancelAnimationFrame(drawLoopRef.current);
      drawLoopRef.current = null;
    }
  };

  const stopGeoWatch = () => {
    if (geoWatchRef.current != null) {
      navigator.geolocation.clearWatch(geoWatchRef.current);
      geoWatchRef.current = null;
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    return () => {
      stopDrawLoop();
      stopGeoWatch();
      clearClipTimer();
      try {
        if (v?.srcObject) {
          v.srcObject.getTracks().forEach((t) => t.stop());
        }
      } catch (_) {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    const loadDrive = async () => {
      try {
        const res = await api.get("/drive/status");
        setDriveStatus(res.data);
      } catch (_) {
        setDriveStatus(null);
      }
    };
    loadDrive();
  }, []);

  const requestGps = async () => {
    setError("");
    setGpsStatus({ ok: false, error: "Requesting GPS…", lat: null, lng: null, accuracy: null });

    if (!navigator.geolocation) {
      setGpsStatus({ ok: false, error: "Geolocation not supported on this device.", lat: null, lng: null, accuracy: null });
      return;
    }

    try {
      const id = navigator.geolocation.watchPosition(
        (pos) => {
          setGpsStatus({
            ok: true,
            error: "",
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => {
          setGpsStatus({ ok: false, error: "GPS permission is required. Please allow location to continue.", lat: null, lng: null, accuracy: null });
          setError("GPS permission is required. Capture/upload are blocked until enabled.");
        },
        { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 },
      );
      geoWatchRef.current = id;
    } catch (_) {
      setGpsStatus({ ok: false, error: "GPS permission is required. Please allow location to continue.", lat: null, lng: null, accuracy: null });
    }
  };

  const startCamera = async () => {
    setError("");
    if (!gpsStatus.ok) {
      setError("GPS is mandatory. Please enable location first.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: true,
      });
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play();
      setHasCamera(true);

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      const draw = () => {
        try {
          if (!videoRef.current) return;
          const vw = videoRef.current.videoWidth || 1280;
          const vh = videoRef.current.videoHeight || 720;

          if (canvas.width !== vw) canvas.width = vw;
          if (canvas.height !== vh) canvas.height = vh;

          ctx.drawImage(videoRef.current, 0, 0, vw, vh);

          const live = liveRef.current;
          if (live?.gpsStatus?.ok) {
            const lines = formatWatermarkText({
              activityType: live.activityType,
              lat: live.gpsStatus.lat,
              lng: live.gpsStatus.lng,
              accuracy: live.gpsStatus.accuracy,
              cases: live.cases,
              sacks: live.sacks,
              extraLine: [live.placeName, live.notes].filter(Boolean).join(" • "),
            });
            drawWatermark(ctx, lines, vw, vh);
          }
        } catch (_) {
          // ignore
        }
        drawLoopRef.current = requestAnimationFrame(draw);
      };

      stopDrawLoop();
      draw();
    } catch (_) {
      setError("Could not access camera/microphone. Please allow permissions.");
    }
  };

  const ensureSessionCreated = async () => {
    if (sessionIdRef.current) return sessionIdRef.current;

    const watermarkText = watermarkLines.join(" | ");
    const createRes = await api.post("/pod-sessions", {
      activity_type: activityType,
      location: { lat: gpsStatus.lat, lng: gpsStatus.lng, accuracy_m: gpsStatus.accuracy },
      watermark_text: watermarkText,
      place_name: placeName || null,
      notes: notes || null,
    });

    sessionIdRef.current = createRes.data.session_id;
    return sessionIdRef.current;
  };

  const enqueueClipUpload = async (blob, idx) => {
    const sessionId = await ensureSessionCreated();

    setUploadQueueCount((c) => c + 1);
    uploadChainRef.current = uploadChainRef.current
      .then(async () => {
        const fd = new FormData();
        fd.append("clip_index", String(idx));
        fd.append("video", blob, `${sessionId}_clip_${String(idx).padStart(3, "0")}.webm`);
        await api.post(`/pod-sessions/${sessionId}/video-clip`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      })
      .catch(() => {
        // keep chain alive
      })
      .finally(() => {
        setUploadQueueCount((c) => Math.max(0, c - 1));
      });

    return uploadChainRef.current;
  };

  const startRecording = async () => {
    setError("");

    if (!gpsStatus.ok) {
      setError("GPS is mandatory. Recording is blocked until enabled.");
      return;
    }
    if (!hasCamera) {
      setError("Camera is not started yet.");
      return;
    }

    if (!window.MediaRecorder) {
      setError(
        "This browser does not support in-app recording. Please use Chrome on Android or desktop Chrome/Edge.",
      );
      return;
    }
    if (!canvasRef.current?.captureStream) {
      setError("This browser cannot record the watermarked preview. Please use a modern Chrome/Edge browser.");
      return;
    }

    try {
      const canvasStream = canvasRef.current.captureStream(30);
      const originalStream = videoRef.current?.srcObject;
      const audioTracks = originalStream ? originalStream.getAudioTracks() : [];
      const mixed = new MediaStream([...canvasStream.getVideoTracks(), ...audioTracks]);

      const candidates = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ];
      const chosen = candidates.find((t) => window.MediaRecorder.isTypeSupported?.(t));

      chunksRef.current = [];
      const rec = chosen ? new MediaRecorder(mixed, { mimeType: chosen }) : new MediaRecorder(mixed);

      rec.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) chunksRef.current.push(evt.data);
      };

      rec.onstop = async () => {
        clearClipTimer();

        const mime = rec.mimeType || "video/webm";
        const blob = new Blob(chunksRef.current || [], { type: mime });
        chunksRef.current = [];

        if (blob.size > 0) {
          const idx = clipIndexRef.current + 1;
          clipIndexRef.current = idx;
          setClipIndex(idx);
          enqueueClipUpload(blob, idx);
        }

        // Resolve any explicit stop waiter
        if (stopResolveRef.current) {
          stopResolveRef.current();
          stopResolveRef.current = null;
        }

        // Auto-continue into next clip
        if (continueAfterStopRef.current) {
          continueAfterStopRef.current = false;
          await new Promise((r) => setTimeout(r, 120));
          await startRecording();
          return;
        }

        setIsRecording(false);
        setIsPaused(false);
      };

      rec.onerror = () => {
        setError("Recording failed to start. Please re-check camera/mic permissions and try again.");
      };

      recorderRef.current = rec;
      rec.start(250);
      setIsRecording(true);
      setIsPaused(false);
      scheduleAutoStop();
    } catch (_) {
      setError("Recording could not start. Try Chrome/Edge and ensure camera + mic permissions are allowed.");
    }
  };

  const togglePause = async () => {
    setError("");
    const rec = recorderRef.current;
    if (!rec) {
      setError("No active recorder found.");
      return;
    }

    try {
      if (rec.state === "recording") {
        rec.pause();
        setIsPaused(true);
        clearClipTimer();
      } else if (rec.state === "paused") {
        rec.resume();
        setIsPaused(false);
        scheduleAutoStop();
      }
    } catch (_) {
      setError("Pause/Resume is not supported on this browser.");
    }
  };

  const stopAndFinish = async () => {
    setError("");
    
    // Allow stopping if actively recording OR if we have clips/photos already
    if (!isRecording && !sessionIdRef.current && clipIndexRef.current === 0 && photos.length === 0) {
      setError("Start recording first.");
      return;
    }

    setFinishing(true);

    // Stop current recorder if active, and wait for onstop to enqueue the last clip
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      continueAfterStopRef.current = false;
      await new Promise((resolve) => {
        stopResolveRef.current = resolve;
        rec.stop();
      });
    }

    // Wait for queued uploads to finish
    try {
      await uploadChainRef.current;
    } catch (_) {
      // ignore
    }

    // Finalize session: update counters/notes (photos already uploaded)
    try {
      const sessionId = await ensureSessionCreated();
      const fd = new FormData();
      fd.append("cases", String(cases));
      fd.append("sacks", String(sacks));
      fd.append("notes", notes || "");
      fd.append("ended_at_iso", new Date().toISOString());
      
      // Only upload photos that weren't already uploaded
      const notUploadedPhotos = photos.filter((p) => !p.uploaded);
      notUploadedPhotos.forEach((p) => {
        fd.append("photos", p.blob, `${sessionId}_photo_${Date.now()}.jpg`);
      });

      const res = await api.post(`/pod-sessions/${sessionId}/finalize`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Clean up local previews
      photos.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch (_) {
          // ignore
        }
      });

      navigate(`/sessions/${res.data.session_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to finalize session.");
    } finally {
      setFinishing(false);
      setIsRecording(false);
      setIsPaused(false);
    }
  };

  const capturePhoto = async () => {
    setError("");
    if (!gpsStatus.ok) {
      setError("GPS is mandatory. Photo capture is blocked until enabled.");
      return;
    }
    if (!hasCamera) {
      setError("Start camera first.");
      return;
    }
    if (photoUploading) {
      setError("Previous photo still uploading. Please wait.");
      return;
    }

    setPhotoUploading(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const vw = canvas.width;
      const vh = canvas.height;
      drawWatermark(ctx, watermarkLines, vw, vh);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      const localUrl = URL.createObjectURL(blob);

      // Ensure session exists
      const sessionId = await ensureSessionCreated();

      // Upload photo immediately
      const fd = new FormData();
      fd.append("photo", blob, `${sessionId}_photo_${Date.now()}.jpg`);

      const res = await api.post(`/pod-sessions/${sessionId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Add to photos list with uploaded status
      setPhotos((prev) => [
        {
          blob,
          url: localUrl,
          uploaded: true,
          drive_file_id: res.data.photo?.drive_file_id,
          drive_web_view_link: res.data.photo?.drive_web_view_link,
        },
        ...prev,
      ]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to capture/upload photo.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1);
      if (removed[0]?.url) URL.revokeObjectURL(removed[0].url);
      return next;
    });
  };

  const primaryLabel = useMemo(() => {
    if (!gpsStatus.ok) return "Enable GPS";
    if (!hasCamera) return "Start camera";
    if (!isRecording) return "Start recording";
    return finishing ? "Saving…" : "Stop & Save";
  }, [gpsStatus.ok, hasCamera, isRecording, finishing]);

  const onPrimary = async () => {
    if (!gpsStatus.ok) return requestGps();
    if (!hasCamera) return startCamera();
    if (!isRecording) return startRecording();
    return stopAndFinish();
  };

  return (
    <div data-testid="new-session-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />

      <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="new-session-title">
              New capture
            </div>
            <div className="mt-2 text-base md:text-lg text-slate-600" data-testid="new-session-subtitle">
              Continuous recording with auto-clips.
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="new-session-status-pills">
              <StatusPill
                testId="status-pill-gps"
                variant={gpsStatus.ok ? "ok" : "warn"}
                label="GPS"
                value={gpsStatus.ok ? "Locked" : "Required"}
              />
              <StatusPill
                testId="status-pill-drive"
                variant={driveStatus?.configured ? "ok" : "warn"}
                label="Drive"
                value={driveStatus ? (driveStatus.configured ? "Configured" : "Not configured") : "Checking"}
              />
              <StatusPill
                testId="status-pill-clip"
                variant={isRecording ? "ok" : "neutral"}
                label="Clip"
                value={`${Math.max(1, Number(clipMinutes) || 5)} min`}
              />
              <StatusPill
                testId="status-pill-clip-index"
                variant={clipIndex > 0 ? "ok" : "neutral"}
                label="Saved clips"
                value={String(clipIndex)}
              />
              <StatusPill
                testId="status-pill-upload-queue"
                variant={uploadQueueCount > 0 ? "warn" : "neutral"}
                label="Uploading"
                value={String(uploadQueueCount)}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="md:hidden">
              <CaptureDetailsDrawer
                activityType={activityType}
                setActivityType={setActivityType}
                cases={cases}
                setCases={setCases}
                sacks={sacks}
                setSacks={setSacks}
                placeName={placeName}
                setPlaceName={setPlaceName}
                notes={notes}
                setNotes={setNotes}
                clipMinutes={clipMinutes}
                setClipMinutes={setClipMinutes}
              />
            </div>
            <Button
              data-testid="new-session-back-button"
              variant="secondary"
              className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
              onClick={() => navigate("/dashboard")}
            >
              Back
            </Button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="hidden lg:block" data-testid="new-session-left-panel">
            <CaptureDetailsPanel
              activityType={activityType}
              setActivityType={setActivityType}
              cases={cases}
              setCases={setCases}
              sacks={sacks}
              setSacks={setSacks}
              placeName={placeName}
              setPlaceName={setPlaceName}
              notes={notes}
              setNotes={setNotes}
            />

            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="clip-settings-card">
              <div className="text-sm font-medium text-slate-900" data-testid="clip-settings-title">Auto clip</div>
              <div className="mt-3 space-y-2">
                <Label data-testid="clip-minutes-label" htmlFor="clipMinutes">Auto-stop every (minutes)</Label>
                <Input
                  id="clipMinutes"
                  data-testid="clip-minutes-input"
                  type="number"
                  min={1}
                  max={60}
                  value={clipMinutes}
                  onChange={(e) => setClipMinutes(e.target.value)}
                  className="pod-input-light bg-white border-slate-300"
                />
                <div className="text-xs text-slate-600" data-testid="clip-minutes-help">
                  Default 5. Recommended 4–8 for weaker phones.
                </div>
              </div>
            </div>
          </div>

          <Card data-testid="camera-stage-card" className="border-slate-200 bg-white shadow-sm lg:col-span-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle data-testid="camera-stage-title">Camera</CardTitle>
                <div className="flex items-center gap-2" data-testid="camera-stage-badges">
                  <Badge
                    data-testid="camera-stage-recording-badge"
                    variant="outline"
                    className={
                      isRecording
                        ? isPaused
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"
                    }
                  >
                    {isRecording ? (isPaused ? "PAUSED" : "REC") : "Idle"}
                  </Badge>
                  <Badge data-testid="camera-stage-photos-badge" variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                    Photos: {photos.length}
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {error ? (
                <div data-testid="capture-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                  {error}
                </div>
              ) : null}

              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-black" data-testid="preview-container">
                <video ref={videoRef} playsInline muted className="hidden" data-testid="raw-video-element" />
                <canvas ref={canvasRef} className="w-full h-auto" data-testid="watermarked-canvas" />

                <div className="absolute top-3 left-3 flex items-center gap-2" data-testid="recording-indicator">
                  <div
                    className={
                      isRecording
                        ? isPaused
                          ? "h-2 w-2 rounded-full bg-amber-400"
                          : "h-2 w-2 rounded-full bg-rose-500"
                        : "h-2 w-2 rounded-full bg-slate-600"
                    }
                    data-testid="recording-dot"
                  />
                  <div className="text-xs text-slate-200" data-testid="recording-status-text">
                    {isRecording ? (isPaused ? "Paused" : `Recording (auto-clip ${Math.max(1, Number(clipMinutes) || 5)}m)`) : "Ready"}
                  </div>
                </div>

                <div className="absolute bottom-3 left-3" data-testid="overlay-counters">
                  <div className="flex items-center gap-2">
                    <Badge data-testid="overlay-cases" variant="outline" className="bg-black/50 border-white/20 text-white">
                      Cases: {cases}
                    </Badge>
                    <Badge data-testid="overlay-sacks" variant="outline" className="bg-black/50 border-white/20 text-white">
                      Sacks: {sacks}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2" data-testid="inline-counters">
                <InlineCounter
                  label="Cases"
                  value={cases}
                  testIdPrefix="cases"
                  onDec={() => setCases((v) => Math.max(0, v - 1))}
                  onInc={() => setCases((v) => v + 1)}
                />
                <InlineCounter
                  label="Sacks"
                  value={sacks}
                  testIdPrefix="sacks"
                  onDec={() => setSacks((v) => Math.max(0, v - 1))}
                  onInc={() => setSacks((v) => v + 1)}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-3" data-testid="primary-controls">
                <Button
                  data-testid="primary-action-button"
                  type="button"
                  className={
                    !gpsStatus.ok
                      ? "rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700"
                      : !hasCamera
                        ? "rounded-2xl bg-slate-900 text-white hover:bg-slate-800"
                        : isRecording
                          ? "rounded-2xl bg-rose-600 text-white hover:bg-rose-700"
                          : "rounded-2xl bg-slate-900 text-white hover:bg-slate-800"
                  }
                  onClick={onPrimary}
                  disabled={finishing}
                >
                  {primaryLabel}
                </Button>

                <Button
                  data-testid="pause-resume-button"
                  type="button"
                  variant="secondary"
                  className={
                    isRecording
                      ? isPaused
                        ? "rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                        : "rounded-2xl bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                      : "rounded-2xl bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                  }
                  onClick={togglePause}
                  disabled={!isRecording}
                >
                  {isPaused ? "Resume" : "Pause"}
                </Button>

                <Button
                  data-testid="photo-action-button"
                  type="button"
                  className="rounded-2xl bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                  onClick={capturePhoto}
                  disabled={!hasCamera || !gpsStatus.ok || photoUploading}
                >
                  {photoUploading ? "Uploading…" : "Take photo"}
                </Button>
              </div>

              <div className="text-xs text-slate-600" data-testid="simple-instructions">
                Recording auto-clips in background. Photos upload immediately. Press Stop &amp; Save when done.
              </div>

              <div data-testid="photos-strip" className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {photos.map((p, idx) => (
                  <div key={p.url} data-testid={`photo-thumb-${idx}`} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <img alt={`photo ${idx + 1}`} src={p.url} className="w-full h-32 object-cover" data-testid={`photo-img-${idx}`} />
                    {p.uploaded && (
                      <div className="absolute top-2 left-2 rounded-full bg-emerald-500 px-2 py-0.5 text-xs text-white">
                        Saved
                      </div>
                    )}
                    <button
                      type="button"
                      className="absolute top-2 right-2 rounded-full bg-white/90 border border-slate-200 px-2 py-1 text-xs text-slate-800 hover:bg-white"
                      onClick={() => removePhoto(idx)}
                      data-testid={`photo-remove-${idx}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
